<template>
  <div class="auth-container">
    <div class="auth-card">
      <div>
        <div class="logo-container">
          <img src="@/assets/query-img.svg" :alt="$t('auth.common.logoAlt')" />
        </div>
        <h5 class="form-title">{{ $t('auth.resetPassword.title') }}</h5>

        <div v-if="loading" class="loading-message">
          {{ $t('auth.resetPassword.verifyingLink') }}
        </div>

        <div v-else-if="error" class="error-container">
          <div class="error-message">{{ errorMessage }}</div>
          <router-link to="/forgot-password" class="auth-button primary-button">
            {{ $t('auth.resetPassword.requestNewLink') }}
          </router-link>
        </div>

        <form v-else-if="!resetComplete" @submit.prevent="resetPassword" class="auth-form">
          <div class="form-field">
            <label class="input-label" for="password">{{ $t('auth.resetPassword.newPasswordLabel') }}</label>
            <div class="password-container">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                id="password"
                v-model="password"
                required
                autocomplete="new-password"
                :class="{ 'error-input': passwordError }"
                :placeholder="$t('auth.resetPassword.newPasswordPlaceholder')"
              />
              <img
                class="eye-icon"
                src="@/assets/eye-icon.svg"
                :alt="$t('auth.common.togglePasswordAlt')"
                @click="togglePassword"
              />
            </div>
          </div>

          <div class="form-field">
            <label class="input-label" for="confirmPassword">{{ $t('auth.resetPassword.confirmPasswordLabel') }}</label>
            <div class="password-container">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-input"
                id="confirmPassword"
                v-model="confirmPassword"
                required
                autocomplete="new-password"
                :class="{ 'error-input': passwordError }"
                :placeholder="$t('auth.resetPassword.confirmPasswordPlaceholder')"
              />
              <img
                class="eye-icon"
                src="@/assets/eye-icon.svg"
                :alt="$t('auth.common.togglePasswordAlt')"
                @click="toggleConfirmPassword"
              />
            </div>
            <div v-if="passwordError" class="error-message">
              {{ passwordErrorMessage }}
            </div>
          </div>

          <button type="submit" class="auth-button primary-button" :disabled="isSubmitting">
            {{ isSubmitting ? $t('auth.resetPassword.submitting') : $t('auth.resetPassword.submit') }}
          </button>
        </form>

        <div v-else class="success-container">
          <div class="success-message">
            {{ $t('auth.resetPassword.success') }}
          </div>
          <router-link to="/loginPage" class="auth-button primary-button">
            {{ $t('auth.resetPassword.loginCta') }}
          </router-link>
        </div>
      </div>
      <div class="copyright">{{ $t('auth.common.copyright') }}</div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');

.auth-container {
  display: flex;
  justify-content: center;
  background: url("@/assets/sign-In-bg.png");
  background-size: cover;
  background-position: center;
  height: 100vh;
  font-family: 'Roboto', sans-serif;
}

.auth-card {
  width: 500px;
  padding: 0 80px;
  background-color: white;
  overflow: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.logo-container {
  width: 100%;
  height: 130px;
  background: black;
  border-radius: 0 0 20px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.form-title {
  text-align: left;
  font-weight: bold;
  margin-top: 40px;
  font-size: 18px;
}

.auth-form {
  margin-top: 20px;
}

.form-field {
  margin-bottom: 20px;
}

.input-label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  text-align: left;
}

.form-input {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #f2f2f2;
  height: 40px;
}

.password-container {
  position: relative;
}

.eye-icon {
  position: absolute;
  top: 12px;
  right: 15px;
  cursor: pointer;
}

.auth-button {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 15px;
  text-align: center;
}

.primary-button {
  background-color: #6c63ff;
  color: white;
  display: inline-block;
  text-decoration: none;
}

.primary-button:hover {
  background-color: #5a54d1;
}

.primary-button:disabled {
  background-color: #a5a0ff;
  cursor: not-allowed;
}

.copyright {
  font-size: 12px;
  margin-bottom: 40px;
  text-align: center;
}

.error-input {
  border: 1px solid red;
}

.error-message {
  color: red;
  font-size: 0.8em;
  margin-top: 5px;
  text-align: left;
}

.success-message {
  color: green;
  font-size: 1em;
  margin-top: 20px;
  margin-bottom: 20px;
  text-align: center;
}

.error-container, .success-container {
  text-align: center;
  margin: 30px 0;
}

.loading-message {
  text-align: center;
  margin: 30px 0;
  font-size: 1em;
  color: #666;
}

@media (max-width: 768px) {
  .auth-card {
    width: 90%;
    padding: 0 30px;
  }

  .logo-container {
    height: 100px;
  }
}
</style>

<script setup>
import { ref, onMounted } from 'vue';
// import { useRouter } from 'vue-router'; // Si tu utilises Vue 3 avec le routeur officiel
import { supabase } from '@/supabase/supabaseClient'; // Assure-toi que le chemin est correct
import i18n from '@/i18n';
import { validatePassword } from '@/utils/passwordValidation';
const t = (key) => i18n.t(key);

// Références pour les données du formulaire
const password = ref('');
const confirmPassword = ref('');
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Références pour la gestion d'état de la page
const isSubmitting = ref(false);
const resetComplete = ref(false);
const passwordError = ref(false);
const passwordErrorMessage = ref('');
const canShowForm = ref(false); // Contrôle l'affichage du formulaire

// const router = useRouter(); // Pour les redirections

// Cette fonction est appelée quand le composant est monté
onMounted(() => {
  supabase.auth.onAuthStateChange(async (event, session) => {
    // On écoute l'événement spécifique à la récupération de mot de passe
    if (event === "PASSWORD_RECOVERY" && session) {
      // L'événement est détecté, on peut afficher le formulaire
      canShowForm.value = true;
    }
  });
});

// Méthode pour mettre à jour le mot de passe
async function resetPassword() {
  // Réinitialiser les erreurs
  passwordError.value = false;
  passwordErrorMessage.value = '';

  // Validation forte : 12 caractères + 4 classes (conforme CNIL 2017-012)
  const pwdCheck = validatePassword(password.value);
  if (!pwdCheck.valid) {
    passwordError.value = true;
    passwordErrorMessage.value = pwdCheck.errors.join(' · ');
    return;
  }
  if (password.value !== confirmPassword.value) {
    passwordError.value = true;
    passwordErrorMessage.value = t('auth.resetPassword.errors.mismatch');
    return;
  }

  isSubmitting.value = true;

  // Appel à l'API Supabase pour mettre à jour l'utilisateur
  const { error } = await supabase.auth.updateUser({
    password: password.value
  });

  if (error) {
    passwordError.value = true;
    passwordErrorMessage.value = error.message || t('auth.resetPassword.errors.generic');
  } else {
    // Le mot de passe a été mis à jour avec succès !
    resetComplete.value = true;
  }

  isSubmitting.value = false;
}

// Fonctions pour afficher/cacher les mots de passe
const togglePassword = () => showPassword.value = !showPassword.value;
const toggleConfirmPassword = () => showConfirmPassword.value = !showConfirmPassword.value;

</script>
